import {
  DependentType,
  DependentUrlKinds,
  SeenVisibilityAsDependent,
  ThresholdType,
} from './types/dependent';
import * as segment from '@fullstory/proto/fs/api/segment';
import {
  CustomFieldBoolType,
  CustomFieldCountsType,
  CustomFieldCountType,
  CustomFieldDateType,
  CustomFieldIdent,
  CustomFieldStringsType,
  CustomFieldStringType,
  CustomFieldType,
  DateRangeValue,
  Duration,
  DurationType,
  ElementStringType,
  ElementType,
  ElementValueIdType,
  NumericRangeType,
  PageRefType,
  PageRefTypeEquals,
  SearchType,
  StringType,
  UiOnlyKinds,
  UiOnlySubkinds,
  UrlCategory,
  UrlType,
  UrlTypeNoAny,
  UrlValue,
} from './types/shared';
import {
  AnyCustomEventType,
  DatalossEventType,
  EventElementKinds,
  EventType,
  EventUnionType,
  EventUrlKinds,
  NamedCustomEventType,
  NamedDefinedEventType,
  SpecEventType,
} from './types/event';
import {
  ANY,
  defaultDependentType,
  defaultEventType,
  defaultNotEventType,
  defaultUserType,
  NONE,
} from './constants';
import { every } from 'lodash';
import { assertExhaustive } from '@fullstory/typeutils';

// This file should only contain assertion functions

/** * Shared assertions ***/

export function isNullType(typ: SearchType): boolean {
  return typ.kind === NONE && typ.subkind === NONE;
}

export const isDefaultType = (data: SearchType): boolean =>
  equalType(defaultUserType, data) ||
  equalType(defaultEventType, data) ||
  equalType(defaultNotEventType, data) ||
  equalType(defaultDependentType, data);

// Checks if the kind, subkind, name?, id? of a SearchType is equal.
export function equalType(a: SearchType, b: SearchType): boolean {
  if (isSearchTypeValueWithName(a) && isSearchTypeValueWithName(b)) {
    return a.kind == b.kind && a.subkind == b.subkind && a.value.name == b.value.name;
  } else if (isSearchTypeWithField(a) && isSearchTypeWithField(b)) {
    return a.kind == b.kind && a.subkind == b.subkind && equalFields(a.field, b.field);
  } else if (isValueIdSearchType(a) && isValueIdSearchType(b)) {
    return a.kind == b.kind && a.subkind == b.subkind && a.value.id == b.value.id;
  }
  return a.kind == b.kind && a.subkind == b.subkind;
}

export function equalFields(af: CustomFieldIdent, bf: CustomFieldIdent): boolean {
  switch (af.type) {
    case CustomFieldType.Name:
      return bf.type === CustomFieldType.Name && af.name === bf.name;
    case CustomFieldType.Spec:
      return bf.type === CustomFieldType.Spec && af.slug === bf.slug;
    default:
      assertExhaustive(af);
  }
}

/** * Custom var assertions ***/

export function isCustomStringType(
  e: SearchType,
): e is CustomFieldStringType<any> | CustomFieldStringsType<any> {
  return (
    e.kind == segment.UserFilter.filter_keys.userVarString ||
    e.kind == segment.UserFilter.filter_keys.userVarStrings ||
    e.kind == segment.DependentEventFilter.filter_keys.pageVarString ||
    e.kind == segment.DependentEventFilter.filter_keys.pageVarStrings ||
    e.kind == segment.DependentEventFilter.filter_keys.userVarString ||
    e.kind == segment.DependentEventFilter.filter_keys.userVarStrings ||
    e.kind == segment.DependentEventFilter.filter_keys.customEventPropertyString ||
    e.kind == segment.DependentEventFilter.filter_keys.customEventPropertyStrings ||
    e.kind == segment.DependentEventFilter.filter_keys.elemVarString ||
    e.kind == segment.DependentEventFilter.filter_keys.elemVarStrings ||
    e.kind == segment.DependentEventFilter.filter_keys.fsevtVarString ||
    e.kind == segment.DependentEventFilter.filter_keys.fsevtVarStrings
  );
}

export function isCustomNumberType(
  e: SearchType,
): e is CustomFieldCountType<any> | CustomFieldCountsType<any> {
  return (
    e.kind == segment.UserFilter.filter_keys.userVarInt ||
    e.kind == segment.UserFilter.filter_keys.userVarInts ||
    e.kind == segment.UserFilter.filter_keys.userVarFloat ||
    e.kind == segment.UserFilter.filter_keys.userVarFloats ||
    e.kind == segment.DependentEventFilter.filter_keys.pageVarInt ||
    e.kind == segment.DependentEventFilter.filter_keys.pageVarInts ||
    e.kind == segment.DependentEventFilter.filter_keys.pageVarFloat ||
    e.kind == segment.DependentEventFilter.filter_keys.pageVarFloats ||
    e.kind == segment.DependentEventFilter.filter_keys.userVarInt ||
    e.kind == segment.DependentEventFilter.filter_keys.userVarInts ||
    e.kind == segment.DependentEventFilter.filter_keys.userVarFloat ||
    e.kind == segment.DependentEventFilter.filter_keys.userVarFloats ||
    e.kind == segment.DependentEventFilter.filter_keys.customEventPropertyInt ||
    e.kind == segment.DependentEventFilter.filter_keys.customEventPropertyInts ||
    e.kind == segment.DependentEventFilter.filter_keys.customEventPropertyFloat ||
    e.kind == segment.DependentEventFilter.filter_keys.customEventPropertyFloats ||
    e.kind == segment.DependentEventFilter.filter_keys.elemVarInt ||
    e.kind == segment.DependentEventFilter.filter_keys.elemVarInts ||
    e.kind == segment.DependentEventFilter.filter_keys.elemVarFloat ||
    e.kind == segment.DependentEventFilter.filter_keys.elemVarFloats ||
    e.kind == segment.DependentEventFilter.filter_keys.fsevtVarInt ||
    e.kind == segment.DependentEventFilter.filter_keys.fsevtVarInts ||
    e.kind == segment.DependentEventFilter.filter_keys.fsevtVarFloat ||
    e.kind == segment.DependentEventFilter.filter_keys.fsevtVarFloats
  );
}

export function isCustomDateType(e: SearchType): e is CustomFieldDateType<any> {
  return (
    e.kind == segment.UserFilter.filter_keys.userVarDate ||
    e.kind == segment.UserFilter.filter_keys.userVarDates ||
    e.kind == segment.DependentEventFilter.filter_keys.pageVarDate ||
    e.kind == segment.DependentEventFilter.filter_keys.pageVarDates ||
    e.kind == segment.DependentEventFilter.filter_keys.userVarDate ||
    e.kind == segment.DependentEventFilter.filter_keys.userVarDates ||
    e.kind == segment.DependentEventFilter.filter_keys.customEventPropertyDate ||
    e.kind == segment.DependentEventFilter.filter_keys.customEventPropertyDates ||
    e.kind == segment.DependentEventFilter.filter_keys.elemVarDate ||
    e.kind == segment.DependentEventFilter.filter_keys.elemVarDates ||
    e.kind == segment.DependentEventFilter.filter_keys.fsevtVarDate ||
    e.kind == segment.DependentEventFilter.filter_keys.fsevtVarDates
  );
}

export function isCustomBoolType(e: SearchType): e is CustomFieldBoolType<any> {
  return (
    e.kind == segment.UserFilter.filter_keys.userVarBool ||
    e.kind == segment.UserFilter.filter_keys.userVarBools ||
    e.kind == segment.DependentEventFilter.filter_keys.pageVarBool ||
    e.kind == segment.DependentEventFilter.filter_keys.pageVarBools ||
    e.kind == segment.DependentEventFilter.filter_keys.userVarBool ||
    e.kind == segment.DependentEventFilter.filter_keys.userVarBools ||
    e.kind == segment.DependentEventFilter.filter_keys.customEventPropertyBool ||
    e.kind == segment.DependentEventFilter.filter_keys.customEventPropertyBools ||
    e.kind == segment.DependentEventFilter.filter_keys.elemVarBool ||
    e.kind == segment.DependentEventFilter.filter_keys.elemVarBools ||
    e.kind == segment.DependentEventFilter.filter_keys.fsevtVarBool ||
    e.kind == segment.DependentEventFilter.filter_keys.fsevtVarBools
  );
}

// isSearchTypeWithField determines if the search type holds a property field.
export function isSearchTypeWithField(
  st: SearchType,
): st is SearchType & { field: CustomFieldIdent } {
  return (st as any).field !== undefined;
}

// isSearchTypeValueWithName determines if the search type holds a name in the value property.
export function isSearchTypeValueWithName(
  st: SearchType,
): st is SearchType & { value: { name: string } } {
  return (st as any).value?.name !== undefined;
}

// isValueIdSearchType determines if the search type holds an id in the value property. This
// id is used to create unique keys from the search type.
export function isValueIdSearchType(st: SearchType): st is SearchType & { value: { id: string } } {
  return (st as any).value?.id !== undefined;
}

export function isDefinedEventSearchType(e: SearchType): e is NamedDefinedEventType {
  return e.kind == UiOnlyKinds.orgEvent && e.subkind == UiOnlySubkinds.definedNamed;
}

/** * EventType assertions ***/

export function isVisitedUrl(
  e: EventType,
): e is UrlType<segment.EventFilter.filter_keys.visitedUrl> {
  return e.kind == segment.EventFilter.filter_keys.visitedUrl;
}

export function isWatchedElement(
  e: EventType,
): e is ElementType<segment.EventFilter.filter_keys.seen> {
  return e.kind == segment.EventFilter.filter_keys.seen;
}

export function isPageRefresh(
  e: EventType,
): e is UrlType<segment.EventFilter.filter_keys.pageRefresh> {
  return e.kind == segment.EventFilter.filter_keys.pageRefresh;
}

export function isAjaxErrorUrl(
  e: EventType,
): e is UrlType<segment.EventFilter.filter_keys.ajaxErrorUrl> {
  return e.kind == segment.EventFilter.filter_keys.ajaxErrorUrl;
}

export function isUncaughtException(
  e: EventType,
): e is StringType<segment.EventFilter.filter_keys.uncaughtException> {
  return e.kind == segment.EventFilter.filter_keys.uncaughtException;
}

export function isConsoleError(
  e: EventType,
): e is StringType<segment.EventFilter.filter_keys.consoleError> {
  return e.kind == segment.EventFilter.filter_keys.consoleError;
}

export function isErrorClick(
  e: EventType,
): e is ElementType<segment.EventFilter.filter_keys.errorClick> {
  return e.kind == segment.EventFilter.filter_keys.errorClick;
}

export function isDataloss(e: EventType): e is DatalossEventType {
  return e.kind == segment.EventFilter.filter_keys.dataloss;
}

export function hasDatalossSourceOrigin(e: EventType): boolean {
  return (
    e.subkind == segment.DatalossOperator.operator_keys.elem ||
    e.subkind == segment.DatalossOperator.operator_keys.navUrl ||
    e.subkind == segment.DatalossOperator.operator_keys.netUrl ||
    e.subkind == segment.DatalossOperator.operator_keys.reqHeader ||
    e.subkind == segment.DatalossOperator.operator_keys.rspHeader ||
    e.subkind == segment.DatalossOperator.operator_keys.reqBody ||
    e.subkind == segment.DatalossOperator.operator_keys.rspBody ||
    e.subkind == segment.DatalossOperator.operator_keys.exception ||
    e.subkind == segment.DatalossOperator.operator_keys.log
  );
}

export function hasDatalossSourceElem(e: EventType): boolean {
  return (
    e.subkind == segment.DatalossOperator.operator_keys.elem ||
    e.subkind == segment.DatalossOperator.operator_keys.inputElem
  );
}

export function hasDatalossSourcePageUrl(e: EventType): boolean {
  return e.subkind == segment.DatalossOperator.operator_keys.navUrl;
}

export function hasDatalossSourceNetUrl(e: EventType): boolean {
  return (
    e.subkind == segment.DatalossOperator.operator_keys.netUrl ||
    e.subkind == segment.DatalossOperator.operator_keys.reqHeader ||
    e.subkind == segment.DatalossOperator.operator_keys.rspHeader ||
    e.subkind == segment.DatalossOperator.operator_keys.reqBody ||
    e.subkind == segment.DatalossOperator.operator_keys.rspBody
  );
}

export function hasDatalossSourceSrcUrl(e: EventType): boolean {
  return (
    e.subkind == segment.DatalossOperator.operator_keys.exception ||
    e.subkind == segment.DatalossOperator.operator_keys.log
  );
}

export function isCssSubkind(e: EventType): boolean {
  return e.subkind == segment.ElementOperator.operator_keys.css;
}

export function isElementSubkind(e: EventType): e is ElementValueIdType<EventElementKinds> {
  return e.subkind == segment.ElementOperator.operator_keys.withElementId;
}

export function isHistoricalElementSubkind(
  e: EventType,
): e is ElementStringType<EventElementKinds> {
  return e.subkind == segment.ElementOperator.operator_keys.withHistoricalElementId;
}

export function isTextSubkind(e: EventType): boolean {
  return (
    e.subkind == segment.ElementOperator.operator_keys.text ||
    e.subkind == segment.ElementOperator.operator_keys.textIsNot ||
    e.subkind == segment.ElementOperator.operator_keys.textStartsWith ||
    e.subkind == segment.ElementOperator.operator_keys.textEndsWith ||
    e.subkind == segment.ElementOperator.operator_keys.textContainsToken ||
    e.subkind == segment.ElementOperator.operator_keys.textNotContainsToken ||
    e.subkind == segment.ElementOperator.operator_keys.textContainsTokenStartsWith
  );
}

export function isNoneOperatorEvent(e: EventType): boolean {
  return (
    e.kind == segment.EventFilter.filter_keys.thrashedCursor ||
    e.kind == segment.EventFilter.filter_keys.crashed ||
    e.kind == segment.EventFilter.filter_keys.lowMemory ||
    e.kind == segment.EventFilter.filter_keys.pinchGesture ||
    e.kind == segment.EventFilter.filter_keys.keepSignal ||
    e.kind == segment.EventFilter.filter_keys.appForcedRestart ||
    e.kind == segment.EventFilter.filter_keys.backgrounded
  );
}

export function isEventElement(e: EventType): boolean {
  return isEventElementKind(e.kind as segment.EventFilter.filter_keys) || e.kind === ANY;
}

interface KeyedEventFilter {
  key: segment.EventFilter.filter_keys;
}

export function isKeyedEventElementKind(k: KeyedEventFilter): k is { key: EventElementKinds } {
  return isEventElementKind(k.key);
}

export function isEventElementKind(e: segment.EventFilter.filter_keys): e is EventElementKinds {
  return (
    e == segment.EventFilter.filter_keys.change ||
    e == segment.EventFilter.filter_keys.rageClick ||
    e == segment.EventFilter.filter_keys.errorClick ||
    e == segment.EventFilter.filter_keys.deadClick ||
    e == segment.EventFilter.filter_keys.suspiciousActivity ||
    e == segment.EventFilter.filter_keys.seen ||
    e == segment.EventFilter.filter_keys.click ||
    e == segment.EventFilter.filter_keys.highlight ||
    e == segment.EventFilter.filter_keys.copy ||
    e == segment.EventFilter.filter_keys.paste
  );
}

export function isAnyCustomEvent(e: SearchType): e is AnyCustomEventType {
  return e.kind == UiOnlyKinds.orgEvent && e.subkind == UiOnlySubkinds.customAny;
}

export function isNamedCustomEvent(e: SearchType): e is NamedCustomEventType {
  return e.kind == UiOnlyKinds.orgEvent && e.subkind == UiOnlySubkinds.customNamed;
}

export function isNamedDefinedEvent(e: SearchType): e is NamedDefinedEventType {
  return e.kind == UiOnlyKinds.orgEvent && e.subkind == UiOnlySubkinds.definedNamed;
}

export function isSpecEvent(e: SearchType): e is SpecEventType {
  return e.kind == UiOnlyKinds.orgEvent && e.subkind == UiOnlySubkinds.specObject;
}

export function isEventUnionType(e: EventType): e is EventUnionType {
  return (
    e.kind == segment.EventFilter.filter_keys.eventSet &&
    e.subkind == segment.EventSetOperator.operator_keys.union
  );
}

export function isVisitedPage(
  e: EventType,
): e is PageRefType<segment.EventFilter.filter_keys.visitedPage> {
  return e.kind == segment.EventFilter.filter_keys.visitedPage;
}

export function isVisitedPageEquals(
  e: EventType,
): e is PageRefTypeEquals<segment.EventFilter.filter_keys.visitedPage> {
  return (
    e.kind == segment.EventFilter.filter_keys.visitedPage &&
    e.subkind == segment.PageRefOperator.operator_keys.equals
  );
}

export function isCustomAPIErrorEvent(e: EventType): boolean {
  return e.kind == UiOnlyKinds.apiError;
}

export function isPinchGesture(e: EventType): boolean {
  return e.kind == segment.EventFilter.filter_keys.pinchGesture;
}

export function isSuspiciousActivity(e: EventType): boolean {
  return e.kind == 'suspiciousActivity';
}

/** * DependentType assertions ***/

export function isScrollDepth(
  d: DependentType,
): d is NumericRangeType<segment.DependentEventFilter.filter_keys.scrollDepth> {
  return d.kind == segment.DependentEventFilter.filter_keys.scrollDepth;
}

export function isTimeDurationRendered(
  d: DependentType,
): d is DurationType<segment.DependentEventFilter.filter_keys.timeDurationRendered> {
  return d.kind == segment.DependentEventFilter.filter_keys.timeDurationRendered;
}

export function isTimeDurationVisible(
  d: DependentType,
): d is DurationType<segment.DependentEventFilter.filter_keys.timeDurationVisible> {
  return d.kind == segment.DependentEventFilter.filter_keys.timeDurationVisible;
}

export function isSeenVisibility(d: DependentType): d is SeenVisibilityAsDependent {
  return d.kind == segment.DependentEventFilter.filter_keys.seenVisibility;
}

export function isUnifiedDependentVisitedUrl(
  d: DependentType,
): d is UrlType<segment.DependentEventFilter.filter_keys.url> {
  return d.kind == segment.DependentEventFilter.filter_keys.url;
}

export function isUnifiedReferrerUrl(
  d: DependentType,
): d is UrlType<segment.DependentEventFilter.filter_keys.referrerUrl> {
  return d.kind == segment.DependentEventFilter.filter_keys.referrerUrl;
}

export function isUnifiedWebSourceFileUrl(
  d: DependentType,
): d is UrlType<segment.DependentEventFilter.filter_keys.webSourceFileUrl> {
  return d.kind == segment.DependentEventFilter.filter_keys.webSourceFileUrl;
}

export function isAjaxRequestUrl(
  d: DependentType,
): d is UrlType<segment.DependentEventFilter.filter_keys.ajaxRequestUrl> {
  return d.kind == segment.DependentEventFilter.filter_keys.ajaxRequestUrl;
}

export function isThresholdType(d: DependentType): d is ThresholdType {
  return d.kind == UiOnlyKinds.threshold;
}

/** * Url assertions ***/

// Checks if the type is a url type with a url value
// Omits url any types
export function isUrlTypeWithValue(
  typ: SearchType,
): typ is UrlTypeNoAny<EventUrlKinds> | UrlTypeNoAny<DependentUrlKinds> {
  return (
    (typ.kind === segment.EventFilter.filter_keys.visitedUrl ||
      typ.kind === segment.EventFilter.filter_keys.pageRefresh ||
      typ.kind === segment.EventFilter.filter_keys.ajaxErrorUrl ||
      typ.kind === segment.DependentEventFilter.filter_keys.url ||
      typ.kind === segment.DependentEventFilter.filter_keys.referrerUrl ||
      typ.kind === segment.DependentEventFilter.filter_keys.webSourceFileUrl) &&
    typ.subkind !== segment.UrlOperator.operator_keys.any &&
    typ.subkind !== segment.UrlOperator.operator_keys.isSet &&
    typ.subkind !== segment.UrlOperator.operator_keys.isUnset
  );
}

export function isStrictlyTokenCategory(category: UrlCategory | null): boolean {
  return category === UrlCategory.query;
}

export function isStrictlyUntokenizedCategory(category: UrlCategory | null): boolean {
  // Full path category can be treated as tokenized or untokenized
  return !isExtendedTokenCategoryOrNotSet(category);
}

export function isTokenCategoryOrNotSet(category: UrlCategory | null): boolean {
  return !category || category === UrlCategory.path || category === UrlCategory.query;
}

export function isUntokenizedCategoryOrNotSet(category: UrlCategory | null): boolean {
  return !category || !isTokenCategoryOrNotSet(category);
}

export function isExtendedTokenCategoryOrNotSet(category: UrlCategory | null): boolean {
  return !category || category === UrlCategory.new_path || isTokenCategoryOrNotSet(category);
}

export function isExtendedUntokenizedCategoryOrNotSet(category: UrlCategory | null): boolean {
  return !category || category === UrlCategory.new_path || isUntokenizedCategoryOrNotSet(category);
}

/** * Value assertions ***/

export function isUrlValue(value: any): value is UrlValue {
  return (
    Object.values(UrlCategory).includes(value?.category) &&
    (typeof value?.value === 'string' || value?.value === null)
  );
}

export function isUrlArray(values: any): values is UrlValue[] {
  return Array.isArray(values) && every(values, isUrlValue);
}

export function isDurationValue(value: any): value is Duration {
  return (value as Duration).scalar !== undefined && (value as Duration).unit !== undefined;
}

export function isStringArray(values: any): values is string[] {
  return Array.isArray(values) && every(values, c => typeof c === 'string');
}

export function isNumberArray(values: any): values is number[] {
  return Array.isArray(values) && every(values, c => typeof c === 'number');
}

export function isDateRangeValue(value: any): value is DateRangeValue {
  return Array.isArray(value) && value.length == 2 && every(value, c => c instanceof Date);
}

// Basically the same as `isCssSubkind` but also confirms the type of `value` for typescript.
export function isCssValue(subkind: any, value: any): value is (string | null)[] {
  return (
    subkind === segment.ElementOperator.operator_keys.css ||
    subkind === segment.ElementOperator.operator_keys.cssIsNot ||
    subkind === segment.CssOperator.operator_keys.matches ||
    subkind === segment.CssOperator.operator_keys.notMatches
  );
}
